import { LitElement, css, html } from 'lit'
import { customElement, property } from 'lit/decorators.js'
import { VERDICT_LABEL, VERDICT_SHORT } from '../lib/recommendation.js'
import type { Recommendation } from '../lib/types.js'
import { tokens } from '../styles/tokens.js'
import './kil-beam.js'

/** Die Antwortkarte: Verdikt, Dauer, Skala und Begründungen. */
@customElement('kil-verdict')
export class KilVerdict extends LitElement {
  static override styles = [
    tokens,
    css`
      :host {
        display: block;
      }

      article {
        background: var(--kil-surface);
        border: 1px solid var(--kil-line);
        border-top: 4px solid var(--kil-accent);
        border-radius: var(--kil-radius);
        padding: 1.5rem 1.25rem 1.25rem;
        display: flex;
        flex-direction: column;
        gap: 1.25rem;
      }

      .headline {
        display: flex;
        flex-direction: column;
        gap: 0.35rem;
      }

      h1 {
        margin: 0;
        font-size: clamp(2.1rem, 8vw, 3.4rem);
        line-height: 0.98;
        font-weight: 800;
        font-stretch: 125%;
        letter-spacing: -0.025em;
        color: var(--kil-accent);
        text-wrap: balance;
      }

      .lead {
        margin: 0;
        font-size: 1.0625rem;
        font-weight: 500;
      }

      .duration {
        display: inline-flex;
        align-items: baseline;
        gap: 0.4rem;
        align-self: flex-start;
        padding: 0.35rem 0.7rem;
        border: 1px solid var(--kil-line-strong);
        border-radius: 999px;
      }

      .duration .value {
        font-family: var(--kil-font-mono);
        font-variant-numeric: tabular-nums;
        font-weight: 500;
      }

      .duration .label {
        font-size: 0.75rem;
        letter-spacing: 0.06em;
        text-transform: uppercase;
        color: var(--kil-ink-dim);
      }

      ul {
        list-style: none;
        margin: 0;
        padding: 0;
        display: flex;
        flex-direction: column;
        gap: 0.7rem;
        border-top: 1px solid var(--kil-line);
        padding-top: 1.1rem;
      }

      li {
        display: grid;
        grid-template-columns: 1.1rem 1fr;
        gap: 0.6rem;
        font-size: 0.9375rem;
        line-height: 1.5;
      }

      .bullet {
        margin-top: 0.45rem;
        width: 0.55rem;
        height: 0.55rem;
        border-radius: 2px;
        rotate: 45deg;
        justify-self: center;
      }

      .bullet.good {
        background: var(--kil-good);
      }
      .bullet.warn {
        background: var(--kil-bad);
      }
      .bullet.info {
        background: var(--kil-line-strong);
      }

      .projection {
        margin: 0;
        padding-top: 1rem;
        border-top: 1px dashed var(--kil-line);
        font-size: 0.875rem;
        color: var(--kil-ink-dim);
      }

      .projection strong {
        font-family: var(--kil-font-mono);
        color: var(--kil-ink);
        font-weight: 500;
      }
    `,
  ]

  @property({ attribute: false }) recommendation!: Recommendation

  private fmt(value: number, digits = 0): string {
    return value.toFixed(digits).replace('.', ',')
  }

  override render() {
    const r = this.recommendation
    if (!r) return null

    return html`
      <article>
        <div class="headline">
          <span class="eyebrow"
            >${r.urgency === 'high' ? 'Dringend' : 'Feuchtevergleich innen / außen'}</span
          >
          <h1>${VERDICT_LABEL[r.verdict]}</h1>
          <p class="lead">${VERDICT_SHORT[r.verdict]}</p>
        </div>

        ${r.durationMinutes
          ? html`<p class="duration">
              <span class="value"
                >${r.durationMinutes[0]}–${r.durationMinutes[1]}&thinsp;min</span
              >
              <span class="label">Stoßlüften, Fenster ganz auf</span>
            </p>`
          : null}

        <kil-beam .indoor=${r.indoor.absolute} .outdoor=${r.outdoor.absolute}></kil-beam>

        <ul>
          ${r.reasons.map(
            (reason) => html`
              <li>
                <span class="bullet ${reason.kind}"></span>
                <span>${reason.text}</span>
              </li>
            `,
          )}
        </ul>

        <p class="projection">
          Nach vollständigem Luftaustausch läge die Raumluft bei
          <strong>${this.fmt(r.projectedIndoorRh)} %</strong> relativer Feuchte (obere
          Abschätzung – Möbel und Wände geben gespeicherte Feuchte nach).
        </p>
      </article>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'kil-verdict': KilVerdict
  }
}
